import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Table, Container, Badge, Form, Row, Col, Button, Pagination, Tabs, Tab } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ApprovalTracking = () => {
    const [trackingData, setTrackingData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [moduleFilter, setModuleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [createdByFilter, setCreatedByFilter] = useState('');
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        const fetchTrackingData = async () => {
            try {
                const response = await axios.get('https://api.admin-panel.nhcholding.com/api/approval-tracking', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });

                if (response.data && response.data.status) {
                    setTrackingData(response.data.data);
                    setFilteredData(response.data.data);
                } else {
                    toast.error('Failed to load tracking data.');
                }
            } catch (error) {
                console.error('Error fetching approval tracking data:', error);
                toast.error('Error loading approval tracking data.');
            } finally {
                setLoading(false);
            }
        };

        fetchTrackingData();
    }, []);

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved & Published', variant: 'success' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            manager_approved: { text: 'Approved by Manager', variant: 'info' },
            pending: { text: 'In Review', variant: 'secondary' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'dark' };
        return <Badge bg={variant}>{text}</Badge>;
    };

    // Filter data based on current filters and active tab
    useEffect(() => {
        const filtered = trackingData.filter(item => {
            const matchesModule = moduleFilter ? item.module === moduleFilter : true;
            const matchesStatus = statusFilter ? item.current_status === statusFilter : true;
            const matchesCreatedBy = createdByFilter ? item.created_by.toLowerCase().includes(createdByFilter.toLowerCase()) : true;
            
            // Filter by tab
            let matchesTab = true;
            if (activeTab === 'offers') {
                matchesTab = item.module === 'Offer Management';
            } else if (activeTab === 'news') {
                matchesTab = item.module === 'News Management';
            }

            return matchesModule && matchesStatus && matchesCreatedBy && matchesTab;
        });
        setFilteredData(filtered);
        setCurrentPage(1); // Reset to first page when filters change
    }, [moduleFilter, statusFilter, createdByFilter, trackingData, activeTab]);

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const clearFilters = () => {
        setModuleFilter('');
        setStatusFilter('');
        setCreatedByFilter('');
    };

    const getUniqueModules = () => {
        return [...new Set(trackingData.map(item => item.module))];
    };

    const getTabFilteredData = (tab) => {
        if (tab === 'offers') {
            return trackingData.filter(item => item.module === 'Offer Management');
        } else if (tab === 'news') {
            return trackingData.filter(item => item.module === 'News Management');
        }
        return trackingData;
    };

    if (loading) {
        return <div>Loading approval tracking data...</div>;
    }

    return (
        <Container fluid>
            <ToastContainer />
            <div className="approval-tracking-page mt-5 mb-5">
                <h3>Approval Tracking</h3>
            </div>

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => {
                    setActiveTab(k);
                    setCurrentPage(1);
                    clearFilters();
                }}
                className="mb-3"
            >
                <Tab eventKey="all" title="All Items">
                    <Form className="mb-3">
                        <Row>
                            <Col md={3}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by Created By"
                                    value={createdByFilter}
                                    onChange={(e) => setCreatedByFilter(e.target.value)}
                                    className="input-brand-shadow"
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    as="select"
                                    value={moduleFilter}
                                    onChange={(e) => setModuleFilter(e.target.value)}
                                    className="input-brand-shadow"
                                >
                                    <option value="">Filter by Module</option>
                                    {getUniqueModules().map(module => (
                                        <option key={module} value={module}>{module}</option>
                                    ))}
                                </Form.Control>
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    as="select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="input-brand-shadow"
                                >
                                    <option value="">Filter by Status</option>
                                    <option value="approved">Approved & Published</option>
                                    <option value="rejected_for_edit">Needs Revision</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="manager_approved">Approved by Manager</option>
                                    <option value="pending">In Review</option>
                                </Form.Control>
                            </Col>
                            <Col md={3} className="text-end">
                                <Button variant="secondary" onClick={clearFilters}>Clear Filters</Button>
                            </Col>
                        </Row>
                    </Form>
                </Tab>

                <Tab eventKey="offers" title="Offers">
                    <Form className="mb-3">
                        <Row>
                            <Col md={4}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by Created By"
                                    value={createdByFilter}
                                    onChange={(e) => setCreatedByFilter(e.target.value)}
                                    className="input-brand-shadow"
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Control
                                    as="select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="input-brand-shadow"
                                >
                                    <option value="">Filter by Status</option>
                                    <option value="approved">Approved & Published</option>
                                    <option value="rejected_for_edit">Needs Revision</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="manager_approved">Approved by Manager</option>
                                    <option value="pending">In Review</option>
                                </Form.Control>
                            </Col>
                            <Col md={4} className="text-end">
                                <Button variant="secondary" onClick={clearFilters}>Clear Filters</Button>
                            </Col>
                        </Row>
                    </Form>
                </Tab>

                <Tab eventKey="news" title="News">
                    <Form className="mb-3">
                        <Row>
                            <Col md={4}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by Created By"
                                    value={createdByFilter}
                                    onChange={(e) => setCreatedByFilter(e.target.value)}
                                    className="input-brand-shadow"
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Control
                                    as="select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="input-brand-shadow"
                                >
                                    <option value="">Filter by Status</option>
                                    <option value="approved">Approved & Published</option>
                                    <option value="rejected_for_edit">Needs Revision</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="manager_approved">Approved by Manager</option>
                                    <option value="pending">In Review</option>
                                </Form.Control>
                            </Col>
                            <Col md={4} className="text-end">
                                <Button variant="secondary" onClick={clearFilters}>Clear Filters</Button>
                            </Col>
                        </Row>
                    </Form>
                </Tab>
            </Tabs>

            <div className="table-brand-shadow">
                <Table responsive striped bordered hover className="text-center">
                    <thead>
                        <tr>
                            <th>Sr. No.</th>
                            <th>Module</th>
                            <th>Item Name</th>
                            <th>Created By</th>
                            <th>Created At</th>
                            <th>Current Status</th>
                            <th>Rejection Reason</th>
                            <th>Approvers</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length === 0 ? (
                            <tr>
                                <td colSpan="8">No approval tracking data available.</td>
                            </tr>
                        ) : (
                            currentItems.map((item, index) => (
                                <tr key={item.id}>
                                    <td>{indexOfFirstItem + index + 1}</td>
                                    <td>
                                        <Badge 
                                            bg={item.module === 'Offer Management' ? 'primary' : 'info'}
                                            className="me-1"
                                        >
                                            {item.module}
                                        </Badge>
                                    </td>
                                    <td>{item.item_name}</td>
                                    <td>{item.created_by}</td>
                                    <td>{new Date(item.created_at).toLocaleString()}</td>
                                    <td>{getStatusBadge(item.current_status)}</td>
                                    <td>{item.rejection_reason || 'N/A'}</td>
                                    <td>
                                        <ul className="list-unstyled mb-0">
                                            {item.approvers.map((approver, idx) => (
                                                <li key={idx} className="mb-2">
                                                    <div className="d-flex flex-column align-items-start">
                                                        <strong>Level {approver.level}</strong>: {approver.role} - 
                                                        <Badge 
                                                            bg={approver.status === 'approved' ? 'success' : 
                                                               approver.status === 'rejected' ? 'danger' : 'secondary'}
                                                            className="ms-1"
                                                        >
                                                            {approver.status}
                                                        </Badge>
                                                        {approver.comment && (
                                                            <div className="text-muted small">
                                                                Comment: {approver.comment}
                                                            </div>
                                                        )}
                                                        {approver.approved_at && (
                                                            <div className="text-muted small">
                                                                Approved at: {new Date(approver.approved_at).toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
                    {activeTab !== 'all' && (
                        <span className="ms-2">
                            (Filtered by {activeTab === 'offers' ? 'Offers' : 'News'})
                        </span>
                    )}
                </div>
                <Pagination className="mb-0">
                    {[...Array(totalPages)].map((_, i) => (
                        <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => handlePageChange(i + 1)}
                        >
                            {i + 1}
                        </Pagination.Item>
                    ))}
                </Pagination>
            </div>
        </Container>
    );
};

export default ApprovalTracking;
